import React, { useMemo } from 'react';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { StoreState } from '@mm/state/store-interfaces';
import * as allPermissions from '@mm/constants/facebook-permissions';
import facebookSDKActions from '@mm/state/actions/facebookActions';
import { trackWebAppGAEvent } from '../../helpers/plugins/website-google-analytics';
import { ErrorReportingUtils } from '@mm/plugins';
import { UserRoleContainer } from '../../hooks/use-route-guard';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { PrimaryButton } from '../PrimaryButton/PrimaryButton';
import facebookLogoSrc from 'assets/facebook-new-logo.svg';
import './FacebookLoginButton.pcss';

export type Props = {
  onSuccess: ({
    facebookToken,
  }: {
    facebookToken: string;
  }) => Promise<any> | void;
  onClick?: () => void;
  askForPermissions?: boolean;
  label?: string;
  permissions?: string;
  className?: string;
  pending?: boolean;
  allowManualFlow?: boolean;
  redirectPath?: string;
  iconSize?: 'sm' | 'md';
};

export const useFacebookLoginButtonOnClick = ({
  permissions = '',
  askForPermissions = false,
  onSuccess = () => new Promise(() => {}),
  onClick,
  allowManualFlow = true,
  redirectPath,
}: Exclude<Props, 'className' | 'label' | 'pending'>) => {
  const dispatch: Dispatch<StoreState> = useDispatch();

  const modalActionsMap = useModalActions();

  const {
    roleFlags: { isLiveChatAgent, isFacebookPageCollaborator, isCollaborator },
  } = UserRoleContainer.useContainer();

  const onButtonClick = async () => {
    if (isLiveChatAgent || isFacebookPageCollaborator || isCollaborator) {
      return modalActionsMap.openAdminAccessRequiredModal();
    }

    onClick?.();
    trackWebAppGAEvent('click', {
      category: 'login',
      label: 'facebook_login_button',
    });
    const fbPermissionsObjects = askForPermissions
      ? allPermissions[permissions]
      : allPermissions.ALL_PERMISSIONS;

    const fbPermissions =
      allPermissions.getArrayOfPermissions(fbPermissionsObjects);

    try {
      const res = allowManualFlow
        ? await dispatch(
            facebookSDKActions.requestPermissionsWithManualFlowFallback(
              fbPermissions,
              undefined,
              redirectPath,
            ),
          )
        : await dispatch(facebookSDKActions.requestPermissions(fbPermissions));
      if (res) {
        onSuccess(res);
        trackWebAppGAEvent('login', {
          category: 'login',
          label: 'login_success',
        });
      }
    } catch (error) {
      ErrorReportingUtils.reportError(`Facebook Login Button: login failure`, {
        extraData: { permissionsRequested: fbPermissions, error },
      });
      trackWebAppGAEvent('login', {
        category: 'login',
        label: 'login_fail',
      });
      return error;
    }
  };

  return onButtonClick;
};

export const FacebookLoginButton = ({ iconSize = 'md', ...props }: Props) => {
  const { className, label = 'Log in with Facebook', pending = false } = props;

  const onButtonClick = useFacebookLoginButtonOnClick(props);

  const classes = useMemo(
    () => ({
      container: classNames('facebook-login-button', className),
      icon: classNames('facebook-login-button__icon', {
        [iconSize]: !!iconSize,
        'fab fa-facebook-f': !pending,
        'fas fa-circle-notch fa-spin': pending,
      }),
    }),
    [className, pending],
  );

  return (
    <PrimaryButton onClick={onButtonClick} className={classes.container}>
      <img src={facebookLogoSrc} alt="Facebook logo" />
      {label}
    </PrimaryButton>
  );
};

export default FacebookLoginButton;
